/**
 * Tenant health status. CRITICAL is accepted from server responses and normalised to WARNING in
 * the portal; it is not produced here.
 */
export const TENANT_HEALTH_STATUS = {
    CRITICAL: 'critical',
    WARNING: 'warning',
    HEALTHY: 'healthy',
    NONE: 'none',
    DISABLED: 'disabled',
    DELETED: 'deleted',
} as const;

/**
 * Tenant health status type
 */
export type TenantHealthStatus = typeof TENANT_HEALTH_STATUS[keyof typeof TENANT_HEALTH_STATUS];

/**
 * Tenant consumption details
 */
export interface ITenantConsumptionDetail {
    tenantId: string;
    tenantName: string;
    tenantStatus: string;
    healthStatus: TenantHealthStatus;
    allocated: number | null;
    consumedFromAllocated: number;
    consumedFromOrgPool: number;
    consumptionRate: number | null;
    remaining: number | null;
}

/**
 * Organization consumption dashboard response
 */
export interface IOrganizationConsumptionDashboard {
    productCode: string;
    startDate: number; // Unix timestamp in seconds
    endDate: number; // Unix timestamp in seconds
    totalCapacity: number;
    totalAllocated: number;
    totalConsumed: number;
    consumedFromAllocated: number;
    consumedFromOrgPool: number;
    remainingPoolCapacity: number;
    tenants: ITenantConsumptionDetail[];
    userTopUpConsumed?: number;
}

/**
 * Request payload for consumption dashboard
 */
export interface IGetConsumptionDashboardRequest {
    productCode: string;
    includeTopUpBreakdown?: boolean;
}

/**
 * Request payload for tenant consumption dashboard summary
 */
export interface IGetTenantConsumptionDashboardSummaryRequest {
    productCode: string;
    tenantId: string;
    startDate?: number; // Unix timestamp in seconds (custom range)
    endDate?: number; // Unix timestamp in seconds (custom range, inclusive)
}

/**
 * Tenant consumption dashboard summary response
 */
export interface ITenantConsumptionDashboardSummary {
    tenantId: string;
    tenantName: string;
    bundleStartDate: number; // Unix timestamp in seconds
    bundleEndDate: number; // Unix timestamp in seconds
    allocated: number;
    consumedFromAllocated: number;
    consumedFromOrganization: number;
    availableForAllocation: number;
    totalCapacity: number;
    totalConsumed: number;
    remainingPoolCapacity: number;
}

/**
 * Request payload for tenant consumption analytics
 */
export interface IGetTenantConsumptionAnalyticsRequest {
    productCode: string;
    tenantId: string;
    startDate: number; // Unix timestamp in seconds
    endDate: number; // Unix timestamp in seconds
    folderKeys?: string[];
    aggregateByOwnership?: boolean;
    timeZoneId?: string; // IANA id (e.g. "Asia/Tokyo") controlling daily bucketing; UTC when omitted
}

export interface ITenantDailyDistributionServiceItem {
    service: string;
    consumedAmount: number;
}

export interface ITenantDailyDistributionItem {
    date: string; // YYYY-MM-DD
    totalConsumedAmount: number;
    services: ITenantDailyDistributionServiceItem[];
}

export interface ITenantServiceConsumptionItem {
    service: string;
    consumedAmount: number;
}

/**
 * Tenant consumption analytics response
 */
export interface ITenantConsumptionAnalytics {
    distribution: ITenantDailyDistributionItem[];
    byService: ITenantServiceConsumptionItem[];
}

export interface IGetTenantFolderConsumptionBreakdownRequest {
    productCode: string;
    tenantId: string;
    startDate: number; // Unix timestamp in seconds
    endDate: number; // Unix timestamp in seconds
    aggregateByOwnership: boolean;
    alwaysIncludeFolderKeys?: string[];
}

export interface ITenantFolderConsumptionBreakdownItem {
    folderId: string;
    folderName: string;
    parentFolderId: string | null;
    fullPath: string;
    level: number;
    hasChildren: boolean;
    descendantCount: number;
    consumedBySelf: number;
    consumedTotal: number;
    processCountSelf: number;
    processCountTotal: number;
    isPersonalWorkspace: boolean;
    isDeletedFolder: boolean;
}

export interface ITenantFolderConsumptionBreakdown {
    tenantId: string;
    productCode: string;
    startDate: number;
    endDate: number;
    aggregateByOwnership: boolean;
    consumptionWithoutFolderContext: number;
    items: ITenantFolderConsumptionBreakdownItem[];
}

export const SYNTHETIC_FOLDER_ID = {
    /**
     * Catch-all for consumption whose folder is unknown (no key/name — an LA without the folder
     * mirror, or usage whose folder is gone entirely).
     */
    unknownFolders: 'unknown-folders',
    /** Container grouping the deleted folders LA still knows by name; pinned to the end of the folder list. */
    deletedFoldersRoot: 'deleted-folders-root',
    personalWorkspacesBucket: 'personal-workspace',
} as const;

/**
 * Sentinel `folderName` the LicenseAccountant emits for the virtual "Personal Workspaces" root (a real folder node,
 * flag-on). The portal swaps this leading path segment for the localized label.
 */
export const PERSONAL_WORKSPACES_ROOT_SENTINEL = 'PersonalWorkspacesRoot';

/**
 * Sentinel `folderName` the BFF gives the synthetic "Deleted" container that groups named deleted
 * folders. The portal swaps this leading path segment for the localized label.
 */
export const DELETED_FOLDERS_ROOT_SENTINEL = 'DeletedFoldersRoot';

export interface IGetTenantProcessConsumptionBreakdownRequest {
    productCode: string;
    tenantId: string;
    startDate: number;
    endDate: number;
    aggregateByOwnership: boolean;
    includeServiceBreakdown?: boolean;
    processNameFilter?: string;
    processType?: string;
    folderKeys?: string[];
    skip: number;
    take: number;
    sortBy: string;
    sortDescending: boolean;
}

export interface ITenantProcessUnitConsumption {
    unit: string;
    quantity: number;
}

export interface ITenantProcessServiceBreakdown {
    service: string;
    unitBreakdown: ITenantProcessUnitConsumption[];
}

export interface ITenantProcessConsumptionItem {
    processKey: string | null;
    processName: string | null;
    processType: string | null;
    folderKey: string | null;
    runs: number;
    consumption: ITenantProcessUnitConsumption[];
    consumptionByService?: ITenantProcessServiceBreakdown[] | null;
    subProcesses?: ITenantProcessConsumptionItem[] | null;
}

export interface ITenantProcessConsumptionBreakdown {
    aggregateByOwnership: boolean;
    totalCount: number;
    items: ITenantProcessConsumptionItem[];
    consumedUnits?: string[];
}

export interface ITenantSummaryData {
    allocated: number | null;
    consumedFromAllocated: number;
    consumedFromOrgPool: number;
    remaining: number | null;
    availableForAllocation: number;
    totalCapacity: number;
    totalConsumed: number;
    remainingPoolCapacity: number;
    productCode: string;
    startDate: number;
    endDate: number;
}
